import { Typography, Box } from "@mui/material";
import Grid from "@mui/material/Grid";
import MzButton from "@markaaz/ui/MzButton";
import MzInput from "@markaaz/ui/MzInputControls";
import MzAlert from "@markaaz/ui/MzAlert";

import * as yup from "yup";
import { useFormik } from "formik";
import TracePanel from "./TracePanel";

const sx = {
  header: {
    display: "flex",
    alignItems: "center"
  },

  title: {
    color: "darkBlue.c100",
    span: {
      color: "negative.c100"
    }
  },

  markaaz: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    mb: 10
  }
};

const validationSchema = yup.object({
  traceId: yup.string().required("TraceID is required")
});

export default function TraceSearchMain({
  data,
  loading,
  loadingSearch,
  matchbookData,
  showNotification,
  showError,
  onSearch,
  onCall,
  onAlertClose
}) {
  const formik = useFormik({
    initialValues: {
      traceId: ""
    },
    onSubmit: async (values) => {
      await onSearch(values.traceId);
    },
    validationSchema,
    enableReinitialize: true,
    validateOnMount: true
  });

  return (
    <Box>
      <Box sx={sx.header}>
        <Box sx={sx.title}>
          <Typography variant="h4">TraceID Search</Typography>
          <Typography variant="body2" mt={3} maxWidth={640}>
            Input a TraceID into the search bar to review details for scenarios exhibiting behaviors that need to be
            investigated.
            <span>&nbsp;*Internal Use Only</span>
          </Typography>
        </Box>
      </Box>
      <Box my={10} maxWidth={640} display="flex" alignItems="center" gap={5}>
        <MzInput
          sx={sx.input}
          name="traceId"
          label="TraceID"
          noBorder
          fullWidth
          error={formik.touched.traceId && !!formik.errors.traceId}
          helperText={formik.touched.traceId && formik.errors.traceId}
          value={formik.values.traceId}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        ></MzInput>
        <MzButton
          variant="contained"
          size="large"
          sx={{ maxWidth: "98px", mt: 4 }}
          disabled={!formik.isValid || formik.isSubmitting}
          loading={loadingSearch}
          onClick={formik.handleSubmit}
        >
          Search
        </MzButton>
      </Box>
      {data && (
        <>
          <Box sx={sx.markaaz}>
            <Grid container spacing={2} alignItems="center">
              {data.metadata.map((item) => {
                return (
                  <>
                    <Grid item md={12}>
                      <Typography color="blue.c100" variant="body2B">
                        {item.name}
                        <Typography color="darkBlue.c100" variant="body2" paddingLeft={1} display="inline">
                          {item.value}
                        </Typography>
                      </Typography>
                    </Grid>
                  </>
                );
              })}
            </Grid>
            <TracePanel data={data.requestBody} title="Markaaz API Request" />
            <TracePanel data={data.responseBody} title="Markaaz API Response" />
            <Box sx={{ display: "flex", gap: "50px" }}>
              <MzButton variant="contained" loading={loading} size="large" sx={{ maxWidth: "200px" }} onClick={onCall}>
                Call Matchbook
              </MzButton>

              {showError && (
                <MzAlert
                  sx={{ alignItems: "center", marginBottom: "30px", width: "394px" }}
                  severity="error"
                  onClose={onAlertClose}
                >
                  {showError}
                </MzAlert>
              )}
            </Box>
          </Box>
        </>
      )}

      {matchbookData && (
        <Box mt={10}>
          <TracePanel title="Matchbook API Response" data={matchbookData.details} />
        </Box>
      )}

      {showNotification.message && (
        <Box mt={10}>
          <MzAlert
            sx={{ alignItems: "center", marginBottom: "30px", width: "394px" }}
            severity={showNotification.type === "success" ? "success" : "error"}
            onClose={onAlertClose}
          >
            Results Not Found. Please check the TraceID entered to ensure it’s accurate.
          </MzAlert>
        </Box>
      )}
    </Box>
  );
}
